/**
 * useOnboardingAutoSave — auto-save hook for the M27 journey.
 *
 * Commandment 28: every onboarding step is interrupt-safe and
 * resume-correct. Form blur, file upload completion, and explicit
 * draft saves all call into this hook. The hook debounces writes
 * by 500ms so a flurry of keypresses lands a single PUT.
 *
 * On unmount / page-hide the hook flushes any pending draft so
 * close-tab-mid-form preserves data.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { api } from "./api";
import { ONBOARDING_JOURNEY_QUERY_KEY, type StepSlug } from "./onboarding";

const DEBOUNCE_MS = 500;

export function useOnboardingAutoSave(slug: StepSlug) {
  const qc = useQueryClient();
  const pendingRef = useRef<Record<string, unknown> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    try {
      const next = await api.updateOnboardingStep(slug, {
        data: pending,
      });
      qc.setQueryData(ONBOARDING_JOURNEY_QUERY_KEY, next);
    } catch {
      // A failed auto-save is non-fatal — restore the pending blob
      // so the next save attempt picks it up.
      pendingRef.current = { ...(pendingRef.current ?? {}), ...pending };
    }
  }, [qc, slug]);

  const save = useCallback(
    (patch: Record<string, unknown>) => {
      pendingRef.current = { ...(pendingRef.current ?? {}), ...patch };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, DEBOUNCE_MS);
    },
    [flush],
  );

  const markStatus = useCallback(
    async (
      status: "in_progress" | "completed" | "skipped",
      extras?: { skipped_reason?: string },
    ) => {
      // Always flush any pending data BEFORE the status transition
      // so the completed_at row carries the final draft.
      await flush();
      const next = await api.updateOnboardingStep(slug, {
        status,
        ...(extras ?? {}),
      });
      qc.setQueryData(ONBOARDING_JOURNEY_QUERY_KEY, next);
      return next;
    },
    [flush, qc, slug],
  );

  // Flush on unmount + when the page hides — covers close-tab.
  useEffect(() => {
    function onHide() {
      void flush();
    }
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
      void flush();
    };
  }, [flush]);

  return { save, flush, markStatus };
}
